import React, { useState } from 'react';
import { Layout, Menu, Button, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  DashboardOutlined,
  TeamOutlined,
  ApiOutlined,
  DollarOutlined,
  SettingOutlined,
  CrownOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  CreditCardOutlined,
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CloudOutlined,
  DatabaseOutlined,
  GlobalOutlined,
  VideoCameraOutlined,
  BulbOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import type { MenuProps } from 'antd';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

interface AdminSidebarProps {
  activeKey: string;
  onChange: (key: string) => void;
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ activeKey, onChange, collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const menuItems: MenuItem[] = [
    {
      key: 'overview',
      icon: <BarChartOutlined />,
      label: '概览',
      className: 'font-semibold'
    },
    {
      type: 'divider' as const
    },
    {
      key: 'users',
      icon: <TeamOutlined />,
      label: '用户管理'
    },
    {
      key: 'generations',
      icon: <ApiOutlined />,
      label: '生成记录'
    },
    {
      key: 'payments',
      icon: <DollarOutlined />,
      label: '支付记录'
    },
    {
      key: 'coupons',
      icon: <GiftOutlined />,
      label: '营销管理'
    },
    {
      type: 'divider' as const
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统配置'
    },
    {
      key: 'status',
      icon: <DashboardOutlined />,
      label: '系统状态'
    },
    {
      key: 'payment-config',
      icon: <CreditCardOutlined />,
      label: '支付配置'
    },
    {
      key: 'api-config',
      icon: <ThunderboltOutlined />,
      label: 'API配置'
    },
    {
      key: 'storage-config',
      icon: <CloudOutlined />,
      label: '存储配置'
    },
    {
      key: 'database',
      icon: <DatabaseOutlined />,
      label: '数据备份'
    },
    {
      type: 'divider' as const
    },
    {
      key: 'inspiration',
      icon: <BulbOutlined />,
      label: '热门灵感'
    },
    {
      key: 'agents',
      icon: <GlobalOutlined />,
      label: '代理商管理'
    },
    {
      type: 'divider' as const
    },
    {
      key: 'back',
      icon: <HomeOutlined />,
      label: '返回首页',
      onClick: () => navigate('/')
    }
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key !== 'back') {
      onChange(e.key);
    }
  };

  return (
    <Sider
      width={220}
      collapsedWidth={80}
      collapsed={collapsed}
      className="hidden lg:block bg-white shadow-md transition-all duration-300"
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 50
      }}
    >
      {/* 管理后台标题 */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 bg-gradient-to-r from-red-50 to-pink-50">
        {!collapsed ? (
          <div className="flex items-center space-x-2">
            <CrownOutlined className="text-2xl text-red-600" />
            <span className="text-lg font-bold bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent">
              管理控制台
            </span>
          </div>
        ) : (
          <CrownOutlined className="text-2xl text-red-600 mx-auto" />
        )}
        
        {/* 折叠按钮 */}
        <Tooltip title={collapsed ? '展开菜单' : '折叠菜单'} placement="right">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => onCollapse(!collapsed)}
            className="text-red-600 hover:text-red-700 hover:bg-red-100"
          />
        </Tooltip>
      </div>

      {/* 管理员信息 */}
      {user && (
        <div className={`p-4 border-b border-gray-200 bg-amber-50 ${collapsed ? 'px-2' : ''}`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center text-white font-semibold shadow-md flex-shrink-0">
              <CrownOutlined />
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-red-700 truncate">
                  管理员
                </div>
                <div className="text-xs text-gray-600 truncate">
                  {user.email}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 导航菜单 */}
      <Menu
        mode="inline"
        selectedKeys={[activeKey]}
        items={menuItems}
        onClick={handleMenuClick}
        className="border-0"
        inlineCollapsed={collapsed}
        style={{ 
          paddingTop: '16px',
          paddingBottom: '16px'
        }}
      />
    </Sider>
  );
};

export default AdminSidebar;

